import { useEffect, useState } from 'react';
import { Stage } from './utils/context-bridge';
import { Devtool } from './components/DOMElements/Devtool';
import { DevtoolConsole } from './components/DOMElements/DevtoolConsole';
import MainMenu from './components/DOMElements/MainMenu';
import { HubInteractionPrompt } from './components/DOMElements/HubInteractionPrompt';
import { DungeonMapScreen } from './components/DOMElements/DungeonMapScreen';
import { DungeonScreen } from './components/DOMElements/DungeonScreen';
import { DungeonGalleryScreen } from './components/DOMElements/DungeonGalleryScreen';
import Game from './components/Game';
import { useSpritesLoader } from './hooks/useSpritesLoader';
import { useGameStore } from './store/gameSlice';
import { useDevtoolStore } from './store/devtoolSlice';
import { useInputServiceLifecycle } from './hooks/useInput';
import { useDevtoolHotkeys } from './hooks/useDevtoolHotkeys';
import { useScreenSize } from './hooks/useScreenSize';
import { InterfaceManager } from './components/DOMElements/InterfaceManager';
import { FadeOverlay } from './components/DOMElements/FadeOverlay';
import { MouseAimLayer } from './components/DOMElements/MouseAimLayer';
import { CinematicOverlay } from './components/DOMElements/CinematicOverlay';
import { LoadingScreen } from './components/DOMElements/LoadingScreen';
import { InputPresetPrompt } from './components/DOMElements/InputPresetPrompt';
import { RendererStatsOverlay } from './components/DOMElements/RendererStatsOverlay';
import { SessionHud } from './components/DOMElements/SessionHud';
import { CoopLoadingOverlay } from './components/DOMElements/CoopLoadingOverlay';
import { ReconnectOverlay } from './components/DOMElements/ReconnectOverlay';
import { DownedBanner } from './components/DOMElements/DownedBanner';
import { useControlsStore } from './store/controlsSlice';
import { useReconnectStore } from './store/reconnectSlice';

const App = () => {
  const gameplay = useGameStore((s) => s.gameplay);
  const screenWidth = useGameStore((s) => s.screenWidth);
  const screenHeight = useGameStore((s) => s.screenHeight);
  const devModeEnabled = useDevtoolStore((s) => s.devModeEnabled);
  const devtoolPanelVisible = useDevtoolStore((s) => s.devtoolPanelVisible);
  const consoleVisible = useDevtoolStore((s) => s.consoleVisible);
  const galleryOpen = useDevtoolStore((s) => s.galleryOpen);
  const inputPresetChosen = useControlsStore((s) => s.inputPresetChosen);
  const reconnecting = useReconnectStore((s) => s.reconnecting);
  const [booted, setBooted] = useState(false);

  useInputServiceLifecycle();
  useDevtoolHotkeys();

  useEffect(() => {
    document.body.classList.toggle('dev-mode', devModeEnabled);
    return () => {
      document.body.classList.remove('dev-mode');
    };
  }, [devModeEnabled]);

  // Keep screenWidth/Height up to date regardless of gameplay state,
  // otherwise the Stage stays at its default size on MainMenu / hub.
  useScreenSize();

  useSpritesLoader();

  if (!booted) {
    return <LoadingScreen onReady={() => setBooted(true)} />;
  }

  return (
    <>
      <div className="DOMContainer">
        {devModeEnabled && devtoolPanelVisible && <Devtool />}
        {devModeEnabled && consoleVisible && <DevtoolConsole />}
        {devModeEnabled && galleryOpen && <DungeonGalleryScreen />}
        {gameplay === 'main-menu' && !reconnecting && <MainMenu />}
        {gameplay === 'dungeon-map' && <DungeonMapScreen />}
        {gameplay === 'dungeon' && <DungeonScreen />}
      </div>

      <CinematicOverlay />
      <HubInteractionPrompt />

      <InterfaceManager />

      <Stage
        width={screenWidth}
        height={screenHeight}
        style={
          gameplay === 'dungeon' || gameplay === 'hub'
            ? undefined
            : { display: 'none' }
        }
      >
        <Game />
      </Stage>
      <FadeOverlay />
      <MouseAimLayer />
      {!inputPresetChosen && <InputPresetPrompt />}
      <RendererStatsOverlay />
      <SessionHud />
      <DownedBanner />
      <CoopLoadingOverlay />
      <ReconnectOverlay />
    </>
  );
};

export default App;
